/**
 * The composer's pure logic, kept out of React so it can be unit-tested without a DOM
 * (`specs/M1-B7-item-card-and-updates.md` §2.3, §6).
 *
 * The composer itself is a `contenteditable`, not TipTap. ADR-001 names TipTap and it is still
 * the target, but round 5 must not add ~15 packages to the lockfile while two other workers
 * share the tree; the toolbar below covers monday's fifteen buttons through the Selection API
 * and the surface the card presents (`applyFormat`, `insertMention`, `serialize`) is the one
 * a TipTap swap would keep. Recorded as a deviation in `tasks/T-round5.md`.
 */
import { formatMondayDayMonth } from '@elizmonday/ui';
import swatches from './composer-swatches.json';
import type { CardMention, CardPerson } from './types';

/** monday's toolbar, in its captured order and with its captured labels `[ic§Composer]`. */
export const TOOLBAR_BUTTONS = [
  { id: 'format', label: 'Format', command: 'formatBlock', glyph: '¶' },
  { id: 'bold', label: 'Bold', command: 'bold', glyph: 'B' },
  { id: 'italic', label: 'Italic', command: 'italic', glyph: 'I' },
  { id: 'underline', label: 'Underline', command: 'underline', glyph: 'U' },
  { id: 'strikethrough', label: 'Strikethrough', command: 'strikeThrough', glyph: 'S' },
  { id: 'color', label: 'Text color', command: 'foreColor', glyph: 'A' },
  { id: 'size', label: 'Size', command: 'fontSize', glyph: 'A↕' },
  { id: 'numbers', label: '1. Numbers', command: 'insertOrderedList', glyph: '1.' },
  { id: 'bullets', label: '• Bullets', command: 'insertUnorderedList', glyph: '•' },
  { id: 'table', label: 'Table', command: 'insertTable', glyph: '▦' },
  { id: 'link', label: 'Link', command: 'createLink', glyph: '🔗' },
  { id: 'align', label: 'Align', command: 'justifyLeft', glyph: '≡' },
  { id: 'line', label: 'Line', command: 'insertHorizontalRule', glyph: '―' },
  { id: 'direction', label: 'Change direction', command: 'direction', glyph: '⇄' },
  { id: 'checklist', label: 'Checklist', command: 'insertChecklist', glyph: '☑' },
] as const;

export type ToolbarButtonId = (typeof TOOLBAR_BUTTONS)[number]['id'];

/* ── the toolbar dropdowns, measured on the sandbox (round 6g) ───────────── */

/**
 * Four of monday's fifteen buttons open a **menu** rather than toggling a mark, and round 5o
 * shipped all four dead because `execCommand` could not express them. They were driven and
 * photographed in round 6g (`tools/browser/capture-item-card-states.mjs toolbar` →
 * `discovery/ui-reference/item-card/q253-toolbar-*.json`), so what follows is measured, not
 * designed: every panel is **264 px wide**, opens at its button's x and at **y 302** — 32 px
 * under the toolbar row — on a **39.5 px** row pitch, radius 2, white,
 * `box-shadow: 0 4px 14px rgba(0,0,0,.2)`.
 */
export const COMPOSER_MENU_WIDTH_PX = 264;
/** Measured pitch: align 119/3, size 277/7, table 395/10, link 79/2 — 39.5 each. */
export const COMPOSER_MENU_ROW_PX = 39.5;

/** monday's `Size` menu, verbatim `[ic§q253-toolbar-size]`. */
export const FONT_SIZES = ['16px', '18px', '24px', '32px', '36px', '48px'] as const;

/**
 * monday's `Text color` grid: **10 × 4 swatches of 20 px on a 24 px pitch**, two tabs
 * (`Text` / `Highlight`) and a `None` row `[ic§q253-toolbar-text-color]`.
 *
 * The 38 values live in `composer-swatches.json`, copied out of the capture. They are
 * **monday's palette, as data** — what a writer picks from — not this product's design
 * tokens, and F7's U1 rule (no colour literal outside `packages/ui/src/tokens`) is right to
 * refuse them in TypeScript. A JSON file is the honest home for a measured table.
 */
export const TEXT_COLOR_SWATCHES: readonly string[] = swatches.swatches;

/** monday's `Table` menu, verbatim and in order `[ic§q253-toolbar-table]`. */
export const TABLE_MENU_ROWS = [
  { id: 'insert-table', label: 'Insert table' },
  { id: 'row-above', label: 'Insert row above' },
  { id: 'row-below', label: 'Insert row below' },
  { id: 'column-left', label: 'Insert column left' },
  { id: 'column-right', label: 'Insert column right' },
  { id: 'add-head', label: 'Add head' },
  { id: 'delete-head', label: 'Delete head' },
  { id: 'delete-column', label: 'Delete column' },
  { id: 'delete-row', label: 'Delete row' },
  { id: 'delete-table', label: 'Delete table' },
] as const;

/** monday's `Align` menu, verbatim — its capitalisation included `[ic§q253-toolbar-align]`. */
export const ALIGN_MENU_ROWS = [
  { id: 'left', label: 'Align left' },
  { id: 'center', label: 'Align Center' },
  { id: 'right', label: 'Align Right' },
] as const;

/** Which toolbar buttons open one of the menus above rather than toggling a mark. */
export const MENU_BUTTON_IDS = ['color', 'size', 'table', 'align'] as const;
export type MenuButtonId = (typeof MENU_BUTTON_IDS)[number];

export function opensMenu(id: string): id is MenuButtonId {
  return (MENU_BUTTON_IDS as readonly string[]).includes(id);
}

/** The measured outer height of a menu with `rows` rows — what the verifier hit-tests. */
export function composerMenuHeight(rows: number): number {
  return Math.round(rows * COMPOSER_MENU_ROW_PX);
}

/**
 * The four insert buttons under the editor, in their captured order `[ic§Composer]`: 32 x 32
 * on a 32 px pitch from x 761.
 *
 * monday's fourth is `Write with AI (Ctrl+j)`, carrying a blue "new" dot. This product has no
 * model behind it, so the slot holds the control P-029 asked for instead — **Formatting**,
 * which reveals the 15-button toolbar. The dot is monday's and is kept: it marks the newest
 * control in the row, which is exactly what this one is.
 */
export const INSERT_BUTTONS = [
  { id: 'mention', label: 'Mention', glyph: '@', isNew: false },
  { id: 'file', label: 'Add file', glyph: '📎', isNew: false },
  { id: 'emoji', label: 'Add emoji', glyph: '☺', isNew: false },
  { id: 'format', label: 'Formatting', glyph: '', isNew: true },
] as const;

/**
 * The `@` trigger, measured against monday: the popup opens on a bare `@` and narrows as more
 * is typed `[ic§@mention popup]`. It closes on a space, because a mention token has no spaces
 * until one is chosen.
 */
export interface MentionTrigger {
  /** The text between the `@` and the caret. */
  query: string;
  /** Offset of the `@` inside the text node, so the token can be replaced in place. */
  start: number;
}

export function findMentionTrigger(textBeforeCaret: string): MentionTrigger | null {
  const at = textBeforeCaret.lastIndexOf('@');
  if (at === -1) return null;
  // An `@` in the middle of a word (an e-mail) is not a mention trigger.
  const before = at === 0 ? '' : textBeforeCaret[at - 1]!;
  if (before !== '' && !/\s/.test(before)) return null;
  const query = textBeforeCaret.slice(at + 1);
  if (/\s/.test(query)) return null;
  return { query, start: at };
}

/** The two special rows monday puts under the people `[ic§@mention popup]`. */
export const MENTION_EVERYONE_ROWS = [
  { id: 'board', label: 'Everyone on this board' },
  { id: 'item', label: 'Everyone on this item' },
] as const;

export interface MentionOption {
  key: string;
  label: string;
  /** `null` for the two "Everyone" rows, which are not a person. */
  person: CardPerson | null;
  kind: 'person' | 'board' | 'item';
}

/**
 * The popup's rows. With a query typed, monday drops the two "Everyone" rows and shows only
 * the matching people — measured: `@Rob` narrowed a 184 px popup to 84 px, one row `[ic§*]`.
 */
export function mentionOptions(people: readonly CardPerson[], query: string): MentionOption[] {
  const rows: MentionOption[] = people.map((person) => ({
    key: `user:${person.id}`,
    label: person.name,
    person,
    kind: 'person',
  }));
  if (query.trim() !== '') return rows;
  return [
    ...rows,
    { key: 'everyone:board', label: MENTION_EVERYONE_ROWS[0].label, person: null, kind: 'board' },
    { key: 'everyone:item', label: MENTION_EVERYONE_ROWS[1].label, person: null, kind: 'item' },
  ];
}

/** The mention this option contributes to `create_update(mentions_list:)`. */
export function mentionFor(option: MentionOption, context: { boardId: string; itemId: string }): CardMention {
  if (option.kind === 'person' && option.person) {
    return { kind: 'user', id: option.person.id, name: option.person.name };
  }
  if (option.kind === 'board') return { kind: 'board', id: context.boardId, name: option.label };
  return { kind: 'item', id: context.itemId, name: option.label };
}

/** `true` when the `Update` button is enabled — monday disables it on an empty editor. */
export function canPost(html: string): boolean {
  // An image on its own is a valid update, so the tag check comes before the text check.
  if (/<img\b/i.test(html)) return true;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;
}

/**
 * **The files a paste or a drop is carrying** (P-299).
 *
 * monday reads the clipboard's *files*, not its HTML: pasting a screenshot uploads the bitmap
 * and inserts it, while pasting text still inserts text
 * (`discovery/behavior-notes/P-299-composer-paste.md`). Two shapes have to be read, because
 * browsers do not agree: Chromium fills `DataTransfer.files` for a screenshot paste, while a
 * paste of a file copied in a file manager fills only `items` with `kind: "file"`. Both are
 * read here, and the same list is de-duplicated by identity so a browser that fills both does
 * not upload the image twice.
 *
 * Returns `[]` for a text paste — which is the signal the caller uses to stand aside and let
 * the editor handle the event itself, so an ordinary paste is never swallowed.
 */
export function filesFromTransfer(transfer: DataTransfer | null | undefined): File[] {
  if (!transfer) return [];
  const out: File[] = [];
  const seen = new Set<File>();
  const push = (file: File | null) => {
    if (!file || seen.has(file)) return;
    // A directory arrives as a zero-byte entry with no type; monday's uploader takes files.
    if (file.size === 0 && file.type === '') return;
    seen.add(file);
    out.push(file);
  };
  for (const file of Array.from(transfer.files ?? [])) push(file);
  for (const item of Array.from(transfer.items ?? [])) {
    if (item.kind !== 'file') continue;
    push(item.getAsFile());
  }
  // Two browsers, two orders: `files` and `items` can name the same bytes twice.
  const unique: File[] = [];
  for (const file of out) {
    if (unique.some((other) => other.name === file.name && other.size === file.size && other.type === file.type)) continue;
    unique.push(file);
  }
  return unique;
}

/** Is this drag carrying files (rather than a text selection being dragged about)? */
export function dragHasFiles(transfer: DataTransfer | null | undefined): boolean {
  if (!transfer) return false;
  return Array.from(transfer.types ?? []).includes('Files');
}

/**
 * Relative time, in monday's own vocabulary: the captured card shows `6m`, `11m`, `17m`,
 * and the board log shows `19m` `[ic§*]`. Beyond a day it falls back to a date.
 */
export function relativeTime(iso: string, now = Date.now()): string {
  const seconds = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${String(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${String(days)}d`;
  return formatMondayDayMonth(iso);
}

/** Human file size, as the Files tab prints it. */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The pinned band. monday's **API** leaves a pinned update where `created_at` puts it
 * `[ua:a21]`, so hoisting is a client behaviour and it lives here, not on the server.
 */
export function orderUpdates<T extends { pinned: boolean; pinnedAt: string | null; createdAt: string }>(
  updates: readonly T[],
): T[] {
  return [...updates].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    if (a.pinned && b.pinned) return (b.pinnedAt ?? '').localeCompare(a.pinnedAt ?? '');
    return b.createdAt.localeCompare(a.createdAt);
  });
}

/** The per-update "…" menu, in the captured order with its two dividers `[ic§The "…" menu]`. */
export function updateMenuRows(update: { pinned: boolean }): { id: string; label: string; divider?: boolean }[] {
  return [
    { id: 'edit', label: 'Edit update' },
    // `Bookmark to update feed` and `Notify me only when mentioned` are monday-feed features
    // with no API surface (q256); they are deliberately absent.
    { id: 'pin', label: update.pinned ? 'Unpin from top' : 'Pin to top', divider: true },
    { id: 'copy-link', label: 'Copy link to update', divider: true },
    { id: 'delete', label: 'Delete update', divider: true },
  ];
}

/** `/boards/<board>/pulses/<item>?update=<id>` — what `Copy link to update` writes. */
export function updatePermalink(boardId: string, itemId: string, updateId: string, origin = ''): string {
  return `${origin}/boards/${boardId}/pulses/${itemId}?update=${updateId}`;
}
